import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Edit a single user setting with an optimistic cache update so UI
 * preferences (theme, map mode) apply instantly and roll back on error.
 * Expects { id, value } where id is the user-setting row id.
 */
export function useEditUserSetting(api, userId, authToken) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, value }) => api.settings.editUserSetting(id, value, authToken),
    onMutate: async ({ id, value }) => {
      await queryClient.cancelQueries({ queryKey: ['userSettings', userId] });
      const previous = queryClient.getQueryData(['userSettings', userId]);
      queryClient.setQueryData(['userSettings', userId], (old) =>
        (old || []).map((s) => (s.id === id ? { ...s, value, rawValue: String(value) } : s))
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['userSettings', userId], context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['userSettings', userId] }),
  });
}

// Changing a default setting also changes every user's resolved settings,
// including the current admin's — refresh both caches.
const invalidateAllSettings = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['defaultSettings'] });
  queryClient.invalidateQueries({ queryKey: ['userSettings'] });
};

export function useAddDefaultSetting(api, authToken) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.settings.addDefaultSetting(data, authToken),
    onSuccess: () => invalidateAllSettings(queryClient),
  });
}

export function useEditDefaultSetting(api, authToken) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.settings.editDefaultSetting(data, authToken),
    onSuccess: () => invalidateAllSettings(queryClient),
  });
}

export function useDeleteDefaultSetting(api, authToken) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.settings.deleteDefaultSetting(id, authToken),
    onSuccess: () => invalidateAllSettings(queryClient),
  });
}
